// API functions for media/file management

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export const mediaApi = {
  // Upload single file
  async upload(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    // Support both folder ID and folder path
    // folder_path is preferred for organized structure (e.g., "pages/abc123")
    if (options.folder_path) {
      formData.append('folder_path', options.folder_path);
    } else if (options.folder) {
      formData.append('folder', options.folder);
    }
    
    if (options.alt_text) formData.append('alt_text', options.alt_text);
    if (options.tags) formData.append('tags', options.tags.join(','));
    
    // Default to optimized WebP for all uploads
    const optimizeForWeb = options.optimize_for_web !== false; // Default true
    const outputFormat = options.output_format || 'webp'; // Default webp
    formData.append('optimize_for_web', optimizeForWeb.toString());
    formData.append('output_format', outputFormat);
    
    const response = await fetch(`${API_URL}/api/v1/media/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return response.json();
  },

  // Upload multiple files
  async uploadMultiple(files, folder = null, folderPath = null) {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (folderPath) {
      formData.append('folder_path', folderPath);
    } else if (folder) {
      formData.append('folder', folder);
    }
    
    const response = await fetch(`${API_URL}/api/v1/media/upload/multiple`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return response.json();
  },

  // List media files
  async list(params = {}) {
    const query = new URLSearchParams();
    if (params.media_type) query.append('media_type', params.media_type);
    if (params.folder) query.append('folder', params.folder);
    if (params.search) query.append('search', params.search);
    if (params.tags) query.append('tags', params.tags.join(','));
    if (params.skip !== undefined) query.append('skip', params.skip);
    if (params.limit !== undefined) query.append('limit', params.limit);
    
    const response = await fetch(`${API_URL}/api/v1/media/?${query}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Get single media file
  async get(id) {
    const response = await fetch(`${API_URL}/api/v1/media/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Update media file metadata
  async update(id, data) {
    const response = await fetch(`${API_URL}/api/v1/media/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Delete media file
  async delete(id) {
    const response = await fetch(`${API_URL}/api/v1/media/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Get file URL - uses /uploads/ path for direct nginx serving
  getFileUrl(filePath) {
    // If it's already a full URL or starts with /uploads/, return as is
    if (filePath.startsWith('http') || filePath.startsWith('/uploads/')) {
      return filePath;
    }
    // Otherwise, construct the /uploads/ path
    return `/uploads/${filePath}`;
  },

  // ===== Folders =====
  
  async createFolder(name, parentId = null) {
    const params = new URLSearchParams({ name });
    if (parentId) params.append('parent_id', parentId);
    
    const response = await fetch(`${API_URL}/api/v1/media/folders?${params}`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  async listFolders(parentId = null) {
    const params = new URLSearchParams();
    if (parentId) params.append('parent_id', parentId);
    
    const response = await fetch(`${API_URL}/api/v1/media/folders?${params}`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  async deleteFolder(folderId) {
    const response = await fetch(`${API_URL}/api/v1/media/folders/${folderId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return response.json();
  },

  // Get media type options
  async getTypeOptions() {
    const response = await fetch(`${API_URL}/api/v1/media/options/types`, {
      headers: getAuthHeaders(),
    });
    return response.json();
  },
};

export default mediaApi;
